import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface CopyResult {
  categories_copied: number;
  categories_skipped: number;
  products_copied: number;
  products_skipped: number;
  errors: string[];
}

export interface CopyCategoriesParams {
  sourceLocationId: string;
  targetLocationIds: string[];
  includeProducts: boolean;
  categoryIds?: string[] | null; // null = all categories
}

export function useCopyCategoryToLocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CopyCategoriesParams): Promise<CopyResult> => {
      const { data, error } = await supabase.rpc('copy_categories_to_locations', {
        p_source_location_id: params.sourceLocationId,
        p_target_location_ids: params.targetLocationIds,
        p_include_products: params.includeProducts,
        p_category_ids: params.categoryIds || null,
      });

      if (error) {
        throw new Error(error.message || 'Failed to copy categories');
      }

      return data as CopyResult;
    },
    onSuccess: (_, params) => {
      // Invalidate categories and products for all affected locations
      params.targetLocationIds.forEach((locationId) => {
        queryClient.invalidateQueries({ queryKey: ['categories', locationId] });
        queryClient.invalidateQueries({ queryKey: ['products', locationId] });
      });
      // Also invalidate for the source location (in case of cache issues)
      queryClient.invalidateQueries({ queryKey: ['categories', params.sourceLocationId] });
      queryClient.invalidateQueries({ queryKey: ['products', params.sourceLocationId] });
    },
  });
}
